import type { StaticEntityType } from "@config";
import { cn } from "@lib/cn";
import { useSlidesLibraryStore } from "@modules/slides-library/shared";
import { IconPlus } from "@tabler/icons-react";
import { useState } from "react";
import { StorydocSparklesIcon } from "@components/ui/icons/sparkles";
import { FEATURE_FLAGS, useFeatureFlag } from "@/lib/feature-flags";
import type { TDesignSlide, TSavedSlide } from "@/shared/types";
import { GenerateWithAiCustomPrompt } from "../../template-slides/components/generate-with-ai-custom-prompt";

// The hover overlay on a saved or team slide, INSIDE THE EDITOR: add it as it
// is, or hand it to the AI as a shape to fill.
//
// A DELIBERATE COPY of the Layouts card's overlay -- same white scrim, same
// two buttons, same sizes, same icons, same open-state highlight (see
// template-slides/components/single-slide-preview.tsx). Two cards sitting in
// the same dialog, a tab apart, that behave the same way should not look
// subtly different; every divergence here would read as a bug rather than as
// a distinction.
//
// The markup is repeated rather than shared because the Layouts card's
// version is inline in a component that also owns pro-locking, category
// badges and its own click handling. Lifting it out is a refactor of that
// card, not of this one.
//
// A saved slide is a layout too -- one the user built -- and up to now the
// only thing they could do with it was drop in the copy it was saved with.
//
// A TDesignSlide is what the generator takes, and a saved slide is not one, so
// the fields it actually reads are filled from the saved slide and the rest
// left empty. The pipeline uses `slideData` for the tree and falls back
// through masterSlide → _id for the layout's id, so a saved slide's own id
// identifies it perfectly well.

export const SavedSlideActions = ({
  slide,
  slideJSON,
  onAdd,
}: {
  slide: TSavedSlide;
  slideJSON: StaticEntityType | null;
  onAdd: () => void;
}) => {
  const [isAiOpen, setIsAiOpen] = useState(false);
  const view = useSlidesLibraryStore((s) => s.view);
  // "Add with AI" is AI on slides, rolled out per org behind its flag.
  const aiSlidesOn = useFeatureFlag(FEATURE_FLAGS.AI_SLIDES);

  // THE PLATFORM LIBRARY IS A PICKER, NOT A WORKBENCH. Out there a card's
  // whole job is to be chosen -- a click selects it -- and there is no
  // document open to add a slide to or to write one into. Both buttons, and
  // the white scrim they sit on, belong to the editor; on the platform the
  // scrim only washed the slide out and swallowed the click.
  if (view !== "editor") return null;

  const design: TDesignSlide = {
    _id: slide._id,
    title: slide.title ?? "",
    imageUrl: "",
    // Empty on purpose: there is no Notion master behind a user's own slide,
    // and the id above identifies it.
    masterSlide: "",
  };

  return (
    <div
      // NOTHING IN HERE IS A CLICK ON THE CARD.
      //
      // The card underneath adds the slide when clicked anywhere on it, and
      // all of this sits inside it -- so without this, opening the AI prompt
      // and clicking into the textarea to type added the slide as it was, a
      // second after the prompt appeared: still on screen, already answered.
      //
      // It has to be caught HERE rather than on the card, because the prompt
      // is a popover: its markup is portalled to document.body, so the card
      // cannot recognise the click by looking at the DOM -- the textarea is
      // nowhere near it. A portal still bubbles through the COMPONENT tree
      // though, and the popover is declared below, so every click the prompt
      // produces passes through this handler on its way to the card.
      onClick={(e) => e.stopPropagation()}
      className={cn(
        "tw-invisible tw-absolute tw-inset-0 tw-z-2 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-rounded-lg tw-bg-white/95 tw-opacity-0 tw-shadow-sm tw-outline tw-outline-1 !tw-outline-offset-0 tw-outline-blue-200 tw-transition-[opacity,visibility] group-hover/slide:tw-visible group-hover/slide:tw-opacity-100",
        isAiOpen && "tw-visible tw-opacity-100",
      )}
    >
      <button
        type="button"
        onClick={onAdd}
        aria-label={`Add slide: ${slide.title}`}
        className="tw-flex tw-h-9 tw-w-[129px] tw-cursor-pointer tw-items-center tw-justify-center tw-gap-1 tw-rounded-lg tw-bg-white tw-font-sans tw-text-sm tw-font-medium tw-text-blue tw-shadow-2xs tw-outline tw-outline-1 !tw-outline-offset-0 tw-outline-blue-100 hover:tw-bg-blue-50"
      >
        <IconPlus
          size={16}
          stroke={1.5}
          className="tw-fill-none tw-text-blue"
          aria-hidden="true"
        />
        Add slide
      </button>
      {aiSlidesOn && (
        <GenerateWithAiCustomPrompt
          isOpen={isAiOpen}
          setIsOpen={setIsAiOpen}
          design={design}
          slide={slideJSON}
          defaultValues={{}}
        >
          <button
            type="button"
            aria-label={`Add slide with AI: ${slide.title}`}
            aria-expanded={isAiOpen}
            aria-haspopup="dialog"
            className={cn(
              "tw-flex tw-h-9 tw-w-[129px] tw-cursor-pointer tw-items-center tw-justify-center tw-gap-1 tw-rounded-lg tw-font-sans tw-text-sm tw-font-medium tw-text-primary tw-shadow-2xs tw-outline tw-outline-1 !tw-outline-offset-0 tw-outline-blue-100 hover:tw-bg-violet-50",
              isAiOpen ? "tw-bg-violet-50" : "tw-bg-white",
            )}
          >
            <StorydocSparklesIcon
              size={16}
              className="tw-text-primary"
              aria-hidden="true"
            />
            Add with AI
          </button>
        </GenerateWithAiCustomPrompt>
      )}
    </div>
  );
};
